import { useEffect, useMemo, useState } from "react";
import {
  useDataProvider,
  useGetIdentity,
  useGetList,
  useNotify,
  type Identifier,
} from "ra-core";
import { CalendarPlus, CheckSquare, Plus, StickyNote } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AddTask } from "@/components/atomic-crm/tasks/AddTask";
import { NoteCreate } from "@/components/atomic-crm/notes";
import type { Contact } from "@/components/atomic-crm/types";
import { CalendarReminderDialog } from "@/modules/calendar/CalendarReminderDialog";
import type { ClientScope } from "@/modules/clients/clientScope";
import { getContactFullName } from "@/modules/clients/clientShowUtils";
import {
  ThreadEditor,
  type ThreadDraftFile,
} from "@/modules/threads/ThreadEditor";
import { createThreadNote } from "@/modules/threads/ThreadReplyComposer";
import { useInvalidateThreads } from "@/modules/threads/useRecordThreads";

/**
 * Create a note, task or meeting for one person. On an account the user
 * picks which contact it belongs to (primary contact by default), so every
 * entry stays tagged to a person.
 */
export type ClientActivityAction = "note" | "task" | "meeting";

export const ClientActivityComposer = ({
  scope,
  primaryContactId,
  initialAction,
}: {
  scope: ClientScope;
  primaryContactId?: Identifier | null;
  /** Open straight into an action (quick actions). */
  initialAction?: ClientActivityAction;
}) => {
  const { data: contacts = [] } = useGetList<Contact>(
    "contacts",
    {
      filter:
        scope.kind === "company"
          ? { "company_id@eq": scope.companyId }
          : { "id@eq": scope.contactId },
      pagination: { page: 1, perPage: 200 },
      sort: { field: "first_name", order: "ASC" },
    },
    { staleTime: 30_000 },
  );
  const defaultContactId = useMemo(() => {
    if (scope.kind === "contact") return String(scope.contactId);
    if (
      primaryContactId != null &&
      contacts.some(
        (contact) => String(contact.id) === String(primaryContactId),
      )
    )
      return String(primaryContactId);
    return contacts[0] ? String(contacts[0].id) : "";
  }, [scope, primaryContactId, contacts]);
  const [contactId, setContactId] = useState(defaultContactId);
  useEffect(() => {
    if (
      !contactId ||
      !contacts.some((contact) => String(contact.id) === contactId)
    ) {
      setContactId(defaultContactId);
    }
  }, [contactId, contacts, defaultContactId]);

  const [mode, setMode] = useState<"note" | null>(
    initialAction === "note" ? "note" : null,
  );
  const [taskOpen, setTaskOpen] = useState(initialAction === "task");
  const [meetingOpen, setMeetingOpen] = useState(initialAction === "meeting");
  const today = new Date().toISOString().slice(0, 10);

  if (scope.kind === "company" && contacts.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        Add a contact to this account to log notes, tasks and meetings.
      </p>
    );
  }

  const selected = contactId || defaultContactId;

  return (
    <div className="space-y-3 rounded-lg border bg-card p-3">
      <div className="flex flex-wrap items-center gap-2">
        {scope.kind === "company" ? (
          <Select value={selected} onValueChange={setContactId}>
            <SelectTrigger
              className="h-8 w-auto min-w-40 max-w-64"
              aria-label="Contact"
            >
              <SelectValue placeholder="Contact" />
            </SelectTrigger>
            <SelectContent>
              {contacts.map((contact) => (
                <SelectItem key={contact.id} value={String(contact.id)}>
                  {getContactFullName(contact)}
                  {String(contact.id) === String(primaryContactId)
                    ? " (primary)"
                    : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}
        <Button
          type="button"
          size="sm"
          variant={mode === "note" ? "default" : "secondary"}
          className="h-8 gap-1.5"
          onClick={() => setMode(mode === "note" ? null : "note")}
          disabled={!selected}
        >
          <StickyNote className="size-4" />
          Note
        </Button>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          className="h-8 gap-1.5"
          onClick={() => setTaskOpen(true)}
          disabled={!selected}
        >
          <CheckSquare className="size-4" />
          Task
        </Button>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          className="h-8 gap-1.5"
          onClick={() => setMeetingOpen(true)}
          disabled={!selected}
        >
          <CalendarPlus className="size-4" />
          Meeting
        </Button>
      </div>

      {mode === "note" && selected ? (
        <NoteCreate key={selected} reference="contacts" contactId={selected} />
      ) : null}

      {taskOpen && selected ? (
        <AddTask
          key={selected}
          contactId={selected}
          open={taskOpen}
          onOpenChange={setTaskOpen}
          hideTrigger
        />
      ) : null}
      <CalendarReminderDialog
        open={meetingOpen}
        onOpenChange={setMeetingOpen}
        dateKey={today}
        variant="meeting"
        initialRecord={{
          contact_id: selected || null,
          company_id: scope.companyId ?? null,
        }}
      />
    </div>
  );
};

/** The people of an account (or the one person), first name order. */
export const useScopeContacts = (scope: ClientScope) =>
  useGetList<Contact>(
    "contacts",
    {
      filter:
        scope.kind === "company"
          ? { "company_id@eq": scope.companyId }
          : { "id@eq": scope.contactId },
      pagination: { page: 1, perPage: 200 },
      sort: { field: "first_name", order: "ASC" },
    },
    { staleTime: 30_000 },
  ).data ?? [];

/** "Everyone" or one person of the account (timeline filter). */
export const ClientPersonSelect = ({
  contacts,
  value,
  onChange,
  primaryContactId,
}: {
  contacts: Contact[];
  value: string;
  onChange: (next: string) => void;
  primaryContactId?: Identifier | null;
}) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger
      size="sm"
      className="w-auto max-w-52 min-w-32 rounded-full text-xs data-[size=sm]:h-7"
      aria-label="Person"
    >
      <SelectValue placeholder="Everyone" />
    </SelectTrigger>
    <SelectContent align="end">
      <SelectItem value="all">Everyone</SelectItem>
      {contacts.map((contact) => (
        <SelectItem key={contact.id} value={String(contact.id)}>
          {getContactFullName(contact)}
          {String(contact.id) === String(primaryContactId) ? " (primary)" : ""}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

/** One "+" for a note, task or meeting of the chosen person. */
export const ClientActivityAddMenu = ({
  scope,
  contactId,
  onNote,
}: {
  scope: ClientScope;
  contactId?: Identifier | null;
  onNote: () => void;
}) => {
  const [taskOpen, setTaskOpen] = useState(false);
  const [meetingOpen, setMeetingOpen] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            size="icon"
            className="size-7"
            aria-label="Add note, task or meeting"
            disabled={contactId == null}
          >
            <Plus className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={onNote}>
            <StickyNote className="size-4" />
            Note
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setTaskOpen(true)}>
            <CheckSquare className="size-4" />
            Task
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setMeetingOpen(true)}>
            <CalendarPlus className="size-4" />
            Meeting
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {taskOpen && contactId != null ? (
        <AddTask
          key={String(contactId)}
          contactId={contactId}
          open={taskOpen}
          onOpenChange={setTaskOpen}
          hideTrigger
        />
      ) : null}
      <CalendarReminderDialog
        open={meetingOpen}
        onOpenChange={setMeetingOpen}
        dateKey={today}
        variant="meeting"
        initialRecord={{
          contact_id: contactId ?? null,
          company_id: scope.companyId ?? null,
        }}
      />
    </>
  );
};

/** Start a thread (note) for one person, right in the timeline. */
export const ClientNoteComposer = ({
  contactId,
  contactName,
  onDone,
}: {
  contactId: Identifier;
  contactName?: string;
  onDone: () => void;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const invalidate = useInvalidateThreads();
  const [text, setText] = useState("");
  const [files, setFiles] = useState<ThreadDraftFile[]>([]);
  const [saving, setSaving] = useState(false);
  const empty = !text.trim() && !files.length;

  const send = async () => {
    if (empty || saving) return;
    setSaving(true);
    try {
      await createThreadNote(dataProvider, {
        target: { kind: "contact", id: contactId },
        text,
        files,
        memberId: identity?.id,
      });
      onDone();
      await invalidate();
    } catch (error) {
      notify(error instanceof Error ? error.message : "Could not add note", {
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="animate-in fade-in slide-in-from-top-2 space-y-1.5 duration-200 motion-reduce:animate-none">
      {contactName ? (
        <p className="text-xs text-muted-foreground">
          New note for{" "}
          <span className="font-medium text-foreground">{contactName}</span>
          <span className="hidden sm:inline"> · Esc to cancel</span>
        </p>
      ) : null}
      <ThreadEditor
        value={text}
        onChange={setText}
        onSubmit={() => void send()}
        onCancel={onDone}
        autoFocus
        files={files}
        onFilesChange={setFiles}
        ariaLabel="New note"
        placeholder="Write a note… type @ to mention"
        className="bg-card"
        actions={
          <>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7"
              onClick={onDone}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              className="h-7"
              disabled={empty || saving}
              isLoading={saving}
              onClick={() => void send()}
            >
              Add note
            </Button>
          </>
        }
      />
    </div>
  );
};
